import React from 'react';
import { Button } from './ui/button';
import { Upload, X, FileText, Loader2 } from 'lucide-react';
import { uploadFile, uploadPrivateFile, generateFilePath } from '../services/firebaseStorage';
import { toast } from 'sonner';

/**
 * File Upload Component with Firebase Storage
 * 
 * Uploads files to Firebase Storage and returns download URLs
 */

const FileUploadPlaceholder = ({ 
  label, 
  accept = "image/*,.pdf", 
  value = null, 
  onChange, 
  language = 'en',
  required = false,
  disabled = false,
  userId = 'temp',
  folder = 'uploads',
  subfolder = null,
  testId = null
}) => {
  const [preview, setPreview] = React.useState(value);
  const [uploading, setUploading] = React.useState(false);

  React.useEffect(() => { setPreview(value); }, [value]);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    
    try {
      // Create local preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      // Upload to Firebase Storage
      const downloadURL = folder === 'verification_docs' || folder === 'jobs_uploads'
        ? await uploadPrivateFile(file, folder, subfolder)
        : await uploadFile(file, generateFilePath(folder, userId, file.name, subfolder));
      
      // Pass Firebase URL to parent
      await onChange(downloadURL);
      
      toast.success(language === 'en' ? 'File uploaded successfully!' : language === 'it' ? 'File caricato con successo!' : language === 'fr' ? 'Fichier téléchargé avec succès!' : 'Datei erfolgreich hochgeladen!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(language === 'en' ? 'Upload failed. Please try again.' : language === 'it' ? 'Caricamento fallito. Riprova.' : language === 'fr' ? 'Échec du téléchargement. Veuillez réessayer.' : 'Upload fehlgeschlagen. Bitte versuchen Sie es erneut.');
      setPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    setPreview(null);
    await onChange(null);
  };

  const content = {
    en: {
      upload: 'Upload File',
      remove: 'Remove',
      noFile: 'No file selected',
      uploading: 'Uploading...'
    },
    de: {
      upload: 'Datei hochladen',
      remove: 'Entfernen',
      noFile: 'Keine Datei ausgewählt',
      uploading: 'Hochladen...'
    },
    it: {
      upload: 'Carica File',
      remove: 'Rimuovi',
      noFile: 'Nessun file selezionato',
      uploading: 'Caricamento...'
    },
    fr: {
      upload: 'Télécharger le Fichier',
      remove: 'Supprimer',
      noFile: 'Aucun fichier sélectionné',
      uploading: 'Téléchargement...'
    }
  };

  const t = content[language] || content.en;

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      
      {!preview ? (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-500 transition-colors">
          {uploading ? (
            <div className="flex flex-col items-center">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-2" />
              <p className="text-sm text-blue-600">{t.uploading}</p>
            </div>
          ) : (
            <>
              <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <input
                type="file"
                accept={accept}
                onChange={handleFileSelect}
                className="hidden"
                id={`file-${label}`}
                disabled={disabled || uploading}
                data-testid={testId ? `${testId}-input` : `file-upload-${folder}-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              />
              <label 
                htmlFor={`file-${label}`} 
                className="cursor-pointer text-blue-600 hover:text-blue-700"
                data-testid={testId ? `${testId}-label` : `file-upload-label-${folder}-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                {t.upload}
              </label>
              <p className="text-xs text-gray-500 mt-1">{t.noFile}</p>
            </>
          )}
        </div>
      ) : (
        <div className="border border-green-300 bg-green-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {preview.startsWith('data:image') || preview.startsWith('https://') ? (
                <img src={preview} alt="Preview" className="w-16 h-16 object-cover rounded" />
              ) : (
                <FileText className="w-16 h-16 text-green-600" />
              )}
              <div>
                <p className="text-sm font-medium text-green-900">
                  {language === 'en' ? 'File uploaded to cloud' : language === 'it' ? 'File caricato nel cloud' : language === 'fr' ? 'Fichier téléchargé dans le cloud' : 'Datei in die Cloud hochgeladen'}
                </p>
                <p className="text-xs text-green-600">
                  ✓ {language === 'en' ? 'Stored in Firebase' : language === 'it' ? 'Salvato in Firebase' : language === 'fr' ? 'Stocké dans Firebase' : 'In Firebase gespeichert'}
                </p>
              </div>
            </div>
            <Button 
              type="button"
              variant="ghost" 
              size="sm" 
              onClick={handleRemove}
              disabled={disabled || uploading}
              data-testid={testId ? `${testId}-remove` : `file-remove-${folder}-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUploadPlaceholder;
